import React from "react";
import {
  ResponsiveContainer, AreaChart, Area, LineChart, Line, XAxis, YAxis,
  Tooltip, CartesianGrid, BarChart, Bar,
} from "recharts";

const AXIS = { stroke: "hsl(var(--muted-foreground))", fontSize: 11 };
const GRID = { stroke: "hsl(var(--border))", strokeOpacity: 0.15 };

function ChartTooltip({ active, payload, label, unit }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border bg-popover px-3 py-2 text-xs shadow-lg">
      <div className="font-medium text-muted-foreground">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="tabular-nums font-semibold" style={{ color: p.color }}>
          {p.value}{unit || ""}
        </div>
      ))}
    </div>
  );
}

export function Sparkline({ data, dataKey = "value", color = "hsl(var(--primary))", height = 48 }) {
  if (!data?.length) return <div className="h-12" />;
  const id = `spark-${dataKey}-${Math.random().toString(36).slice(2, 6)}`;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.35} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <Area type="monotone" dataKey={dataKey} stroke={color} strokeWidth={2} fill={`url(#${id})`} isAnimationActive />
      </AreaChart>
    </ResponsiveContainer>
  );
}

export function LineTrend({ data, dataKey = "value", xKey = "date", unit = "", color = "hsl(var(--primary))", height = 260 }) {
  if (!data?.length) return null;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
        <CartesianGrid vertical={false} {...GRID} />
        <XAxis dataKey={xKey} tickLine={false} axisLine={false} {...AXIS} minTickGap={30} />
        <YAxis tickLine={false} axisLine={false} {...AXIS} width={40} domain={["auto", "auto"]} />
        <Tooltip content={<ChartTooltip unit={unit} />} />
        <Line type="monotone" dataKey={dataKey} stroke={color} strokeWidth={2.5} dot={false} activeDot={{ r: 4 }} isAnimationActive />
      </LineChart>
    </ResponsiveContainer>
  );
}

export function Bars({ data, dataKey = "value", xKey = "label", unit = "", color = "hsl(var(--primary))", height = 240 }) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
        <CartesianGrid vertical={false} {...GRID} />
        <XAxis dataKey={xKey} tickLine={false} axisLine={false} {...AXIS} />
        <YAxis tickLine={false} axisLine={false} {...AXIS} width={40} />
        <Tooltip content={<ChartTooltip unit={unit} />} cursor={{ fill: "hsl(var(--accent))", opacity: 0.3 }} />
        <Bar dataKey={dataKey} fill={color} radius={[6, 6, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

export function Ring({ value, max, size = 88, stroke = 9, color = "hsl(var(--primary))", label, sublabel }) {
  const pct = max ? Math.min(1, value / max) : 0;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  return (
    <div className="relative grid place-items-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="hsl(var(--muted))" strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke}
          strokeDasharray={c} strokeDashoffset={c * (1 - pct)} strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 0.6s ease" }} />
      </svg>
      <div className="absolute text-center">
        <div className="font-display text-lg font-bold tabular-nums leading-none">{label}</div>
        {sublabel && <div className="text-[10px] text-muted-foreground">{sublabel}</div>}
      </div>
    </div>
  );
}
